import { inject } from '@angular/core';
import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { catchError, filter, Observable, Subject, switchMap, take } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { jwtDecode } from 'jwt-decode';
import { Tokens } from '../../shared/interfaces/personal-data.interface';
import { compareAsc } from 'date-fns';
import { Router } from '@angular/router';

export function authInterceptor(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const auth: AuthService = inject(AuthService);
  const tokenSubject: Subject<Tokens | null> = new Subject<Tokens | null>();
  const router = inject(Router);

  if (
    request.url.includes('/login') ||
    request.url.includes('/refresh') ||
    request.url.includes('/assets')
  )
    return next(request);

  if (request.url.includes('/logout')) return next(addToken(request, auth.authToken()));

  if (isTokenExpired(auth.authToken())) {
    console.warn('Auth token expired');
    if (isTokenExpired(auth.updateToken())) {
      logout(auth, router);
      throw new Error('Refresh token expired');
    } else {
      tokenSubject.next(null);
      if (!auth.isTokenUpdating()) {
        return auth.refreshToken().pipe(
          switchMap((newTokens: Tokens) => {
            if (newTokens) {
              tokenSubject.next(newTokens);
              return next(addToken(request, auth.authToken()));
            }
            logout(auth, router);
            throw new Error('Token expired');
          }),
          catchError(err => {
            logout(auth, router);
            throw err;
          })
        );
      } else {
        return tokenSubject.pipe(
          filter(token => token !== null),
          take(1),
          switchMap(() => next(addToken(request, auth.authToken())))
        );
      }
    }
  }
  return next(addToken(request, auth.authToken()));
}

function addToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}

function logout(auth: AuthService, router: Router) {
  auth.logout().subscribe(() => router.navigate(['/login']));
}

function isTokenExpired(token: string): boolean {
  if (!token) return true;
  const { exp }: { exp: number } = jwtDecode(token);
  return compareAsc(new Date(exp * 1000), new Date()) < 1;
}
